/**
 * Applique les couleurs de MARQUE d'une entité à un sous-arbre React.
 *
 * ## Pourquoi une conversion, et pas `style={{ color: hex }}`
 *
 * Les composants du Design System ne lisent pas des couleurs, ils lisent des
 * **tokens** (`bg-primary`, `ring-ring`, `text-primary-foreground`). Poser un
 * hex en ligne ne teindrait que l'élément touché et laisserait tout son
 * intérieur — anneaux de focus, états survolés, bordures — sur les couleurs
 * Argalis. Redéfinir les variables CSS sur un conteneur, à l'inverse, teint
 * l'arbre entier sans qu'aucun composant n'ait à le savoir.
 *
 * ⚠️ Les variables du thème portent un triplet HSL **sans** `hsl()`
 * (`--primary: 174 100% 21%`), parce que Tailwind les compose avec une opacité
 * (`hsl(var(--primary) / 0.5)`). Y écrire un hex casse silencieusement toutes
 * les classes à opacité : la couleur ne s'applique simplement plus. D'où la
 * conversion — ce n'est pas un détail d'implémentation, c'est le contrat.
 *
 * ## Le texte posé DESSUS
 *
 * `--primary-foreground` ne peut pas rester blanc : une marque en jaune clair
 * rendrait ses libellés illisibles. Il se DÉRIVE, et depuis PORT il se dérive de
 * la **luminance relative** (`contrasteMarque.encreSurMarque`), plus de la
 * luminosité HSL : celle-ci rendait du blanc sur `#FFD400` à **1,43:1**, soit
 * des libellés quasi invisibles sur la page qui porte l'identité du client. Un
 * écran qui ment sur la lisibilité est pire qu'un écran sans couleurs — et il
 * n'y a plus d'« aperçu » ici : `useBrandThemeOnRoot` habille le portail réel.
 */

import { useEffect } from 'react'
import type { CSSProperties } from 'react'

import { canauxSRGB, ratioDeContraste } from './contrasteMarque'
import { decisionDeMarque } from './decisionDeMarque'
import type { EncreDeMarque } from './contrasteMarque'

/**
 * UX-2 — le fond contre lequel l'anneau de focus se mesure : `--background` du
 * thème clair (`0 0% 100%`, `index.css:95`), le fond NOMINAL de l'espace et des
 * pages publiques.
 *
 * ⚠️ Mesurer sur le fond clair — et non sur le fond courant — est ce qui garantit
 * qu'une marque tenant déjà 3:1 ne voie **aucun** changement, dans les deux
 * thèmes. Seul le REPLI a besoin de suivre le thème (voir `ENCRE_DU_THEME`).
 */
const FOND_DE_PAGE = '#FFFFFF'
/** WCAG 2.2 §1.4.11 — un composant NON TEXTUEL (l'anneau) : 3:1 sur son fond. */
const SEUIL_NON_TEXTUEL = 3
/**
 * 🔴 **Le repli de l'anneau est l'encre DU THÈME, jamais une teinte fixe.**
 *
 * La première version posait l'anthracite `#1A1A1A` de la cascade d'encre. Sur
 * le fond clair, impeccable (17:1) ; sur le fond SOMBRE (`220 18% 10%`,
 * `index.css:290`) il tombe à ~1,2:1 — un anneau invisible, c'est-à-dire
 * exactement le défaut qu'on corrige, déplacé d'un thème à l'autre. Et le
 * thème sombre EST atteignable sur `/espace` : `index.html:29-31` pose la
 * classe `.dark` depuis `localStorage('argalis-theme')`, donc tout navigateur
 * ayant servi au back-office en sombre rend l'espace en sombre.
 *
 * `var(--foreground)` est l'encre que le thème garantit lisible sur SON fond :
 * `:root` la pose sombre, `.dark` la pose claire, `html.neutral` la redéfinit
 * aussi. Un seul repli, juste des deux côtés — mesuré par le gardien.
 *
 * ⚠️ La substitution a lieu sur `documentElement`, où les trois déclarations
 * vivent : la valeur reste un TRIPLET, donc `hsl(var(--ring) / 0.5)` continue
 * de composer (voir l'en-tête). ⛔ Ne pas y écrire un `hsl(...)` complet.
 */
const ENCRE_DU_THEME = 'var(--foreground)'

/** `#0A7D74` → `[174, 85, 27]` (H, S %, L %). `null` si la chaîne n'est pas un hex. */
export function hexToHsl(hex: string): [number, number, number] | null {
    // L'analyse du hex vit dans `contrasteMarque` et NULLE PART ailleurs : si la
    // conversion HSL et la mesure de contraste n'acceptaient pas exactement les
    // mêmes chaînes, une charte pourrait teindre l'aplat sans que l'encre soit
    // recalculée — deux jumeaux, et la garde sur celui que personne n'emprunte.
    const canaux = canauxSRGB(hex)
    if (!canaux) return null

    const r = canaux[0] / 255
    const g = canaux[1] / 255
    const b = canaux[2] / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const l = (max + min) / 2
    const d = max - min

    if (d === 0) return [0, 0, Math.round(l * 100)]

    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    let h: number
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6

    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)]
}

function triplet([h, s, l]: [number, number, number]): string {
    return `${h} ${s}% ${l}%`
}

/**
 * Les trois encres de la cascade ③, exprimées dans le format des variables.
 *
 * ⚠️ Le contrat de sortie est un TRIPLET HSL sans `hsl()` (« 0 0% 10% »), pas un
 * hex : Tailwind compose ces variables avec une opacité. Rendre `#1A1A1A` ici
 * désactiverait silencieusement toutes les classes à opacité — voir l'en-tête.
 *
 * Une table plutôt qu'un `hexToHsl(encre)` à la volée : elle est TOTALE (TypeScript
 * exige les trois clés, un quatrième barreau de cascade ne compilerait pas sans sa
 * traduction) et elle n'a pas de branche `null` à inventer. Sa fidélité aux hex de
 * `contrasteMarque` est vérifiée par un test, pas supposée.
 */
const ENCRE_EN_TRIPLET: Record<EncreDeMarque, string> = {
    '#FFFFFF': '0 0% 100%',
    '#1A1A1A': '0 0% 10%',
    '#000000': '0 0% 0%',
}


/**
 * Le couple indissociable {aplat, encre lisible} d'une couleur de charte.
 *
 * ⚠️ Exister en tant que COUPLE est le garde-fou : `--primary-foreground`,
 * `--secondary-foreground` et `--accent-foreground` sortent tous les trois d'ici.
 * Trois appels séparés à `foregroundFor` se corrigeraient un par un, et le
 * correctif d'aujourd'hui aurait pu ne réparer que le premier des trois.
 */
function aplatDeMarque(
    hex: string | null | undefined,
): { fond: string; encre: string; anneau: string } | null {
    if (!hex) return null
    /**
     * 🔴 **UNE SEULE ENCRE SUR TOUTE LA PAGE — et c'est la SURFACE qui plie,
     * dans les limites où elle peut plier sans cesser d'être la marque.**
     *
     * Le PO, le 2026-09-09 : « On ne peut pas avoir le bouton avec le texte
     * noir dedans si on a le texte blanc sur la gauche. » Le défaut venait des
     * deux seuils employés sur une même couleur. Deux réponses ont échoué avant
     * celle-ci — deux seuils (deux encres, refusé), puis assombrir toute
     * surface (un jaune devenait brun). `decisionDeMarque` tranche les deux :
     * elle pousse la surface INTERACTIVE au plus de 15 points de luminosité, et
     * renonce au blanc au-delà. Le raisonnement et les douze mesures qui le
     * fondent vivent là-bas, pas ici.
     *
     * ⛔ **`--primary` porte la surface POUSSÉE**, pas la couleur brute : c'est
     * lui qui remplit les boutons, donc du petit texte. L'aplat de la page
     * d'accès prend la MÊME surface poussée — il porte un sous-titre et des
     * repères, du petit texte lui aussi — mais `PortalAuthLayout` le peint par
     * un style en ligne tiré de `decisionDeMarque`, jamais par ce token.
     */
    const { encre, surfaceInteractive } = decisionDeMarque(hex)
    const fondHex = surfaceInteractive ?? hex
    const hsl = hexToHsl(fondHex)
    if (!hsl) return null
    /**
     * UX-2 — **l'anneau garde la marque tant qu'elle se VOIT** (≥ 3:1 sur le
     * fond de page), sinon il prend l'encre du thème. Une charte jaune donnait
     * un anneau à 1,43:1 : le clavier perdait sa position sur la page même qui
     * demande un mot de passe.
     * ⚠️ Une marque qui tient 3:1 ne voit AUCUN changement — c'est testé, avec
     * un témoin de la bande « encre sombre mais anneau lisible » (`#DD44FF`).
     */
    const anneau =
        ratioDeContraste(fondHex, FOND_DE_PAGE) >= SEUIL_NON_TEXTUEL
            ? triplet(hsl)
            : ENCRE_DU_THEME
    return { fond: triplet(hsl), encre: ENCRE_EN_TRIPLET[encre], anneau }
}

/**
 * Style à poser sur le conteneur d'aperçu.
 *
 * Retourne `undefined` quand aucune couleur exploitable n'est fournie : le
 * sous-arbre garde alors le thème Argalis, ce qui est le comportement juste —
 * une entité sans charte ne doit pas voir son aperçu virer au gris.
 *
 * `--ring` suit la primaire : l'anneau de focus est ce que le répondant voit le
 * plus souvent au clavier, le laisser sur la couleur Argalis trahirait l'aperçu
 * précisément là où il compte — SAUF quand la primaire ne tient pas 3:1 sur le
 * fond (voir `aplatDeMarque`) : un anneau invisible n'est pas un anneau. Le
 * repli est alors l'encre du thème, qui reste lisible en clair comme en sombre.
 */
export function brandThemeStyle(
    primary: string | null | undefined,
    secondary?: string | null,
): CSSProperties | undefined {
    const p = aplatDeMarque(primary)
    const s = aplatDeMarque(secondary)
    if (!p && !s) return undefined

    const style: Record<string, string> = {}
    if (p) {
        style['--primary'] = p.fond
        style['--primary-foreground'] = p.encre
        style['--ring'] = p.anneau
    }
    if (s) {
        style['--secondary'] = s.fond
        style['--secondary-foreground'] = s.encre
        // `accent` est le token des états SURVOLÉS du DS : sans lui, passer la
        // souris sur une option repasserait en gris Argalis au milieu d'un
        // aperçu aux couleurs de l'organisme.
        style['--accent'] = s.fond
        style['--accent-foreground'] = s.encre
    }
    return style as CSSProperties
}


/**
 * Applique les couleurs de marque à la RACINE du document, le temps d'une page.
 *
 * 🔴 **Un style posé sur un conteneur n'atteint pas les portails.** `PopoverContent`,
 * `DialogContent` et le calendrier sont rendus par Radix dans `document.body`,
 * hors du sous-arbre React qui les déclare. Sur la page publique d'inscription,
 * le résultat était exactement celui-là : anneaux de focus et bordures aux
 * couleurs de l'organisme — ils sont dans l'arbre — mais **survol du sélecteur
 * de civilité et sélecteur de date restés au vert Argalis**, parce qu'ils
 * s'ouvrent dans un portail (retour PO du 2026-08-20).
 *
 * Corriger composant par composant reviendrait à passer le thème à chaque
 * `PopoverContent` du dépôt, et à recommencer au prochain menu ajouté. La racine
 * est le seul ancêtre commun d'un portail et de son déclencheur.
 *
 * ⚠️ Réservé aux pages qui **sont** l'application le temps de leur affichage :
 * pages publiques (inscription, signature, questionnaire), jamais un aperçu
 * intégré — celui-ci teindrait l'écran qui l'entoure. Pour un aperçu, c'est
 * `brandThemeStyle` sur son conteneur.
 *
 * ⚠️ Le nettoyage n'est pas optionnel : sans lui, la couleur d'un organisme
 * survivrait à la navigation et teindrait la page suivante.
 */
export interface BrandThemeOptions {
    /**
     * PORT-008 §8.2 — poser les variables en `!important`.
     *
     * ⚠️ Sans cela, l'espace externe repasse INTÉGRALEMENT au monochrome
     * plateforme dès qu'un visiteur a choisi la palette neutre : `html.neutral`
     * déclare `--primary`, `--primary-foreground`, `--accent` et `--ring` en
     * `!important` (`index.css`), et une déclaration `!important` de feuille de
     * style bat une inline ordinaire. L'organisme perdrait ses couleurs sur la
     * seule page où elles sont *son* identité, sans qu'aucun test de couleur ne
     * rougisse — la variable est bien posée, elle est simplement dominée.
     *
     * Réservé aux pages qui SONT l'application : l'espace externe. Une page du
     * back-office ne doit pas court-circuiter le choix d'accessibilité de son
     * utilisateur.
     */
    important?: boolean
}

export function useBrandThemeOnRoot(
    primary: string | null | undefined,
    secondary?: string | null,
    options?: BrandThemeOptions,
): void {
    const important = options?.important ?? false
    useEffect(() => {
        const style = brandThemeStyle(primary, secondary)
        if (!style) return
        const root = document.documentElement
        const entries = Object.entries(style as Record<string, string>)
        const priorite = important ? 'important' : ''
        for (const [name, value] of entries) root.style.setProperty(name, value, priorite)
        return () => {
            for (const [name] of entries) root.style.removeProperty(name)
        }
    }, [primary, secondary, important])
}
